import React from 'react';
import './SettingStatus.css';
import {
	ErrorAlertIcon,
	InfoAlertIcon,
	CheckCircleIcon,
	SettingNoteDisabledStatusIcon,
	SettingNoteWarningAlert,
} from '../../../../Icons/AlertIcons/AlertIcons';

// Re-export setting note message constants for convenience
export { SETTING_NOTE_MESSAGES } from './SettingNotes';

export const enum SettingAlertType {
	Info = 'info',
	AlertMessageContent = 'alertMessageContent',
	Warning = 'warning',
	Error = 'error',
	Important = 'important',
	Plain = 'plain',
	SettingNoteInfo = 'settingNoteInfo',
	SettingNoteWarning = 'settingNoteWarning',
}

export const enum SettingNoteType {
	Warning = 'warning',
	Info = 'info',
}

export interface SettingStatusProps {
	statusType: SettingAlertType;
	message: string;
	icon?: React.ReactNode;
	className?: string;
}

export interface SettingNoteProps {
	message: string;
	type: SettingNoteType;
	className?: string;
}

export const SettingNote: React.FC<SettingNoteProps> = ({
	message,
	type,
	className = '',
}) => {
	const icon =
		type === SettingNoteType.Warning ? (
			<SettingNoteWarningAlert className="warning-alert-icon" />
		) : (
			<SettingNoteDisabledStatusIcon className="admin-icon" />
		);
	return (
		<SettingStatus
			statusType={
				type === SettingNoteType.Warning
					? SettingAlertType.SettingNoteWarning
					: SettingAlertType.SettingNoteInfo
			}
			message={message}
			icon={icon}
			className={className}
		/>
	);
};

export const SettingStatus: React.FC<SettingStatusProps> = ({
	statusType,
	message,
	icon,
	className = '',
}) => {
	const getStatusClassName = () => {
		switch (statusType) {
			case SettingAlertType.SettingNoteWarning: // due to static status background for setting note should be transparent
				return 'settings-status-settingNoteWarning';
			case SettingAlertType.SettingNoteInfo:
				return 'settings-status-settingNote';
			case SettingAlertType.Important:
				return 'settings-status-important';
			case SettingAlertType.Warning:
				return 'settings-status-warning';
			case SettingAlertType.Error:
				return 'settings-status-error';
			case SettingAlertType.Plain:
				return 'settings-status-plain';
			case SettingAlertType.AlertMessageContent:
				return 'settings-status-alertMessageContent';
			case SettingAlertType.Info:
			default:
				return 'settings-status-info';
		}
	};

	const getIcon = () => {
		switch (statusType) {
			case SettingAlertType.AlertMessageContent:
				return icon ? icon : <CheckCircleIcon />;
			case SettingAlertType.Warning:
				return icon ? icon : <InfoAlertIcon color="#D15700" />;
			case SettingAlertType.Important:
				return icon ? icon : <InfoAlertIcon color="#9F8800" />;
			case SettingAlertType.Error:
				return icon ? icon : <ErrorAlertIcon />;
			case SettingAlertType.Info:
			case SettingAlertType.Plain:
			default:
				return icon ? icon : <InfoAlertIcon />;
		}
	};

	return (
		<div className={`settings-status ${getStatusClassName()} ${className}`.trim()}>
			{getIcon()}
			<span>{message}</span>
		</div>
	);
};
